import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { formatToBrowserTime, formatToUtcIso } from '../../utils/timeFormat';
import Grid from '../../components/grid/Grid';
import AuditFilter from '../../components/filters/auditFilter/AuditFilter';
import { getGlobalAuditLogs } from '../../services/AuditLogs';
import { DEFAULT_DAYS_AUDITLOGS_DATA } from '../../utils/constants';
import AuditPagination from '../../components/auditPagination/AuditPagination';
import './GlobalAuditLogs.css';

const DEFAULT_RANGE_DAYS = DEFAULT_DAYS_AUDITLOGS_DATA;
const DEFAULT_LIMIT = 100;

const GlobalAuditLogs = () => {
    const { t } = useTranslation();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // pagination-related states
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [currentCursor, setCurrentCursor] = useState(null);
    const [nextCursor, setNextCursor] = useState(null);
    const [totalItems, setTotalItems] = useState(null);
    const [lastSearchParams, setLastSearchParams] = useState(null);

    const headers = [
        { key: 'id', label: 'ID', visible: false },
        { key: 'user_name', label: 'Username', width: '10%' },
        { key: 'audit_category', label: 'Category', width: '15%' },
        { key: 'context_type', label: 'Context', width: '8%' },
        { key: 'message', label: 'Message', width: '40%' },
        { key: 'created_date', label: 'Audit Time', width: '17%' },
        { key: 'device_name', label: 'Device', width: '10%' },
    ];

    const mapApiResponseToGridData = (arr) =>
        (arr ?? []).map((item, index) => ({
            id: index + 1,
            device_name: item?.device_name ?? item?.PK ?? '',
            audit_category: item?.audit_category ?? '',
            message: item?.message ?? '',
            context_type: item?.context_type ?? '',
            user_name: item?.user_name ?? '',
            created_date: item?.created_date
                ? formatToBrowserTime(item.created_date, false, true)
                : ''
        }));

    const buildDefaultSearch = () => {
        const now = dayjs();
        const start = now.subtract(DEFAULT_RANGE_DAYS, 'day');
        return {
            limit: DEFAULT_LIMIT,
            start_date: formatToUtcIso(start, false),
            end_date: formatToUtcIso(now, false)
        };
    };

    //loadData to handle cursor and pagination state
    const loadData = async (searchData, cursor = null, replacePagination = false) => {
        try {
            setLoading(true);

            const params = { ...searchData, limit: searchData.limit ?? limit };
            if (cursor) params.lastKey = cursor;


            const raw = await getGlobalAuditLogs(params);

            const list = Array.isArray(raw?.items) ? raw.items : [];

            const mapped = mapApiResponseToGridData(list);
            setData(mapped);

            if (typeof raw?.totalItems === 'number') {
                setTotalItems(raw.totalItems);
            }

            setCurrentCursor(cursor ?? null);
            setNextCursor(raw?.lastKey ?? null);

            if (replacePagination) {
                setLastSearchParams(params);
            }
        } catch (err) {
            console.error('Global audit logs fetch failed:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const defaultSearch = buildDefaultSearch();
        setLimit(defaultSearch.limit);
        loadData(defaultSearch, null, true);
    }, []);

    const onSearch = (searchData) => {
        const sd = { ...searchData, limit: DEFAULT_LIMIT };
        setLimit(sd.limit);
        setCurrentCursor(null);
        setNextCursor(null);
        setTotalItems(null);
        setLastSearchParams(sd);
        loadData(sd, null, true);
    };

    const handleNavigate = (cursor, searchData) => {
        const sd = searchData ?? lastSearchParams ?? buildDefaultSearch();
        loadData(sd, cursor, false);
    };


    return (
        <div className="audit-page-wrapper">
            <div className="audit-header">
                <h4>{t('Global Audit Logs')}</h4>
            </div>

            <div>
                <AuditFilter
                    onSearch={(data) => onSearch(data)}
                    onReset={() => onSearch(buildDefaultSearch())}
                />
            </div>

            <div className="audit-content">
                <Grid
                    headers={headers}
                    data={data}
                    rowsPerPage={DEFAULT_LIMIT}
                    canMultiSelect={false}
                    loading={loading}
                    showPagination={false}
                />
            </div>

            <AuditPagination
                currentCursor={currentCursor}
                nextCursor={nextCursor}
                onNavigate={handleNavigate}
                loading={loading}
                totalItems={totalItems}
                limit={limit}
                searchData={lastSearchParams}
            />
        </div>
    );
};

export default GlobalAuditLogs;
